import { useState, useRef, useEffect } from 'react'
import { useEditMode } from '../contexts/EditModeContext'
import { lumi } from '../lib/lumi'
import {Pencil, Check, X, Loader2} from 'lucide-react'
import toast from 'react-hot-toast'

interface EditableTextProps {
  /** Collection name in lumi.entities */
  collection: string
  /** Document _id */
  documentId: string
  /** Field path to update — supports nested like "content.title" */
  field: string
  /** Current value to display */
  children: React.ReactNode
  /** The raw text/html value for editing */
  value?: string
  /** Type of editor */
  type?: 'text' | 'textarea' | 'html'
  /** CSS class for the wrapper in view mode */
  className?: string
  /** Inline style for the wrapper in view mode */
  style?: React.CSSProperties
  /** Callback after successful save — receives new value and field path */
  onSave?: (newValue: string, field?: string) => void
  /** Render as this HTML tag in view mode */
  as?: keyof JSX.IntrinsicElements
  /** Additional props for the wrapper element */
  wrapperProps?: Record<string, any>
}

const EditableText = ({
  collection,
  documentId,
  field,
  children,
  value = '',
  type = 'text',
  className = '',
  style,
  onSave,
  as: Tag = 'div',
  wrapperProps = {},
}: EditableTextProps) => {
  const { isEditMode } = useEditMode()
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value)
  // Keep a local copy of the last saved value so we don't depend solely on parent re-rendering
  const [lastSavedValue, setLastSavedValue] = useState(value)
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

  // Sync with prop when parent updates value (e.g. after refetch)
  useEffect(() => {
    setEditValue(value)
    setLastSavedValue(value)
  }, [value])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      if (inputRef.current instanceof HTMLTextAreaElement) {
        inputRef.current.style.height = 'auto'
        inputRef.current.style.height = inputRef.current.scrollHeight + 'px'
      }
    }
  }, [isEditing])

  // Not in edit mode or no document — just render children
  if (!isEditMode || !documentId) {
    return <Tag className={className} style={style} {...wrapperProps}>{children}</Tag>
  }

  /** Fetch the full current document using get() when available, otherwise fallback to list({filter:{_id}}) */
  const fetchCurrentDoc = async (): Promise<any | null> => {
    const entity = (lumi.entities as any)[collection]
    if (!entity) return null
    // Try .get(id) first
    if (typeof entity.get === 'function') {
      try {
        const doc = await entity.get(documentId)
        if (doc && (doc._id || doc.id)) return doc
        // Some SDKs return {data: doc}
        if (doc?.data) return doc.data
      } catch (err) {
        // fall through to list fallback
        console.warn('[EditableText] get() failed, falling back to list():', err)
      }
    }
    // Fallback: list with filter
    try {
      const res = await entity.list({ filter: { _id: documentId }, limit: 1 })
      return res?.list?.[0] || res?.[0] || null
    } catch (err) {
      console.error('[EditableText] list() fallback failed:', err)
      return null
    }
  }

  const handleSave = async () => {
    // Compare against the last saved value, not the (potentially stale) prop value
    if (editValue === lastSavedValue) {
      setIsEditing(false)
      return
    }
    setSaving(true)
    try {
      const parts = field.split('.')
      const updateData: any = {}

      if (parts.length === 1) {
        updateData[field] = editValue
      } else {
        // Nested field: fetch current doc and merge at the top-level key
        const doc = await fetchCurrentDoc()
        if (!doc) {
          throw new Error(
            `Could not load document "${documentId}" from collection "${collection}" to update nested field "${field}".`
          )
        }
        const topKey = parts[0]
        // Deep-clone the top-level object so we don't mutate the cached doc
        const topValue =
          doc[topKey] && typeof doc[topKey] === 'object'
            ? JSON.parse(JSON.stringify(doc[topKey]))
            : {}
        let cursor: any = topValue
        for (let i = 1; i < parts.length - 1; i++) {
          if (!cursor[parts[i]] || typeof cursor[parts[i]] !== 'object') {
            cursor[parts[i]] = {}
          }
          cursor = cursor[parts[i]]
        }
        cursor[parts[parts.length - 1]] = editValue
        updateData[topKey] = topValue
      }

      updateData.updatedAt = new Date().toISOString()

      // Guard: ensure we're actually sending a meaningful update
      const meaningfulKeys = Object.keys(updateData).filter((k) => k !== 'updatedAt')
      if (meaningfulKeys.length === 0) {
        throw new Error('No field changes to persist (empty update payload).')
      }

      await (lumi.entities as any)[collection].update(documentId, updateData)

      // Update internal baseline so subsequent comparisons work correctly
      setLastSavedValue(editValue)

      toast.success('Saved!', {
        style: { background: '#2C2C2C', color: '#fff', borderRadius: '8px' },
        duration: 1500,
      })
      // Notify parent with new value + field so it can refresh its local state
      onSave?.(editValue, field)
      setIsEditing(false)
    } catch (error: any) {
      console.error('[EditableText] Error saving:', error)
      toast.error(error?.message || 'Error saving changes')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setEditValue(lastSavedValue)
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancel()
    }
    if (type === 'text' && e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    }
  }

  // Inline tags that cannot contain block elements like <div>
  const inlineTags = new Set(['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'a', 'label', 'li', 'dt', 'dd', 'td', 'th'])
  const isInlineTag = inlineTags.has(Tag as string)
  // Use a neutral wrapper: span for inline tags, div for block tags
  const EditingWrapper = (isInlineTag ? 'span' : 'div') as React.ElementType

  // Editing state
  if (isEditing) {
    return (
      <EditingWrapper style={{ position: 'relative', display: isInlineTag ? 'inline-block' : 'block' }} className="group/editing">
        {type === 'textarea' || type === 'html' ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={editValue}
            onChange={(e) => {
              setEditValue(e.target.value)
              e.target.style.height = 'auto'
              e.target.style.height = e.target.scrollHeight + 'px'
            }}
            onKeyDown={handleKeyDown}
            className="w-full px-3 py-2 bg-white border-2 border-[#7F1734] rounded-lg shadow-lg text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#7F1734]/30 resize-none min-h-[60px] font-inherit text-inherit leading-relaxed"
            style={{ fontFamily: 'inherit', fontSize: 'inherit' }}
          />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full px-3 py-2 bg-white border-2 border-[#7F1734] rounded-lg shadow-lg text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#7F1734]/30 font-inherit text-inherit"
            style={{ fontFamily: 'inherit', fontSize: 'inherit' }}
          />
        )}
        {/* Action buttons — rendered as a span to stay DOM-valid inside any parent tag */}
        <span
          style={{
            position: 'absolute',
            bottom: '-40px',
            right: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            zIndex: 50,
          }}
        >
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1 px-3 py-1.5 bg-[#7F1734] text-white text-xs font-medium rounded-md shadow-lg hover:bg-[#6b1229] transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={handleCancel}
            disabled={saving}
            className="flex items-center gap-1 px-3 py-1.5 bg-white text-[#2C2C2C] text-xs font-medium rounded-md shadow-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <X size={12} />
            Cancel
          </button>
        </span>
      </EditingWrapper>
    )
  }

  // View mode with edit hover indicator
  // Use inline-block wrapper for inline tags to keep overlays valid
  const viewStyle = isInlineTag
    ? { ...style, position: 'relative' as const, display: 'inline-block' as const }
    : { ...style, position: 'relative' as const }

  return (
    <Tag
      className={`${className} group/editable cursor-pointer`}
      style={viewStyle}
      onClick={() => setIsEditing(true)}
      {...wrapperProps}
    >
      {children}
      {/* Hover border + edit icon — use span to stay DOM-valid */}
      <span
        style={{
          position: 'absolute',
          inset: 0,
          border: '2px dashed transparent',
          borderRadius: '8px',
          pointerEvents: 'none',
          transition: 'border-color 200ms',
        }}
        className="group-hover/editable:!border-[#7F1734]/40"
      />
      <span
        style={{
          position: 'absolute',
          top: '-12px',
          right: '-12px',
          opacity: 0,
          pointerEvents: 'none',
          zIndex: 30,
          transition: 'opacity 200ms',
        }}
        className="group-hover/editable:!opacity-100"
      >
        <span
          style={{
            width: '28px',
            height: '28px',
            background: '#7F1734',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 6px rgba(0,0,0,0.15)',
          }}
        >
          <Pencil size={12} className="text-white" />
        </span>
      </span>
    </Tag>
  )
}

export default EditableText
